import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  listIdeas,
  decideIdeas,
  sendIdeasToDraft,
  triggerDiscovery,
  processNextEditorialTask,
} from "@/lib/editorial.functions";
import { toast } from "sonner";
import { formatDate } from "@/lib/site";
import { useRealtimeInvalidate } from "@/hooks/useRealtimeInvalidate";
import { WorkerPanel } from "@/components/admin/WorkerPanel";

export const Route = createFileRoute("/admin/ideas")({
  validateSearch: (search: Record<string, unknown>) => ({
    status: (typeof search.status === "string" ? search.status : "new") as string,
  }),
  component: IdeasPage,
});

const STATUS_TABS = [
  { value: "new", label: "New" },
  { value: "approved", label: "Approved" },
  { value: "queued", label: "Queued" },
  { value: "drafted", label: "Drafted" },
  { value: "rejected", label: "Rejected" },
];

function IdeasPage() {
  const qc = useQueryClient();
  const navigate = Route.useNavigate();
  const { status } = Route.useSearch();
  const setStatus = (s: string) => navigate({ search: { status: s }, replace: true });
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data: ideas, refetch } = useQuery({
    queryKey: ["admin", "ideas", status],
    queryFn: () => listIdeas({ data: { status } }),
    refetchInterval: 10000,
  });
  useRealtimeInvalidate([
    { table: "article_ideas", queryKey: ["admin", "ideas", status] },
  ]);


  function toggle(id: string) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }
  function clearSel() {
    setSelected(new Set());
  }

  const discover = useMutation({
    mutationFn: () => triggerDiscovery({ data: { max: 15 } }),
    onSuccess: (r: any) =>
      toast.success(
        `Scanned ${r.candidates} candidates · created ${r.ideasCreated} new ideas${
          r.queuedDrafts ? ` · ${r.queuedDrafts} breaking queued` : ""
        }${r.refill?.queued ? ` · ${r.refill.queued} autopilot queued` : ""}`,
      ),
    onError: (e: any) => toast.error(e.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "ideas"] }),
  });

  const decide = useMutation({
    mutationFn: (decision: "approved" | "rejected" | "archived") =>
      decideIdeas({ data: { ids: [...selected], decision } }),
    onSuccess: (_r, decision) => {
      toast.success(`${selected.size} idea(s) ${decision}`);
      clearSel();
    },
    onError: (e: any) => toast.error(e.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "ideas"] }),
  });

  const send = useMutation({
    mutationFn: () => sendIdeasToDraft({ data: { ids: [...selected] } }),
    onSuccess: (r: any) => {
      toast.success(`Queued ${r.taskCount} draft task(s). Worker drafts one per minute.`);
      clearSel();
    },
    onError: (e: any) => toast.error(e.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "ideas"] }),
  });

  const processOne = useMutation({
    mutationFn: () => processNextEditorialTask({ data: {} }),
    onSuccess: (r: any) => {
      if (r?.status === "idle") toast.info("No pending tasks.");
      else toast.success(`Processed ${r.kind}: ${r.outcome}`);
      refetch();
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <div className="space-y-8">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="headline-display text-3xl">Idea backlog</h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-xl">
            <strong>Step 1 of the editorial flow.</strong> Discovery scans tier A–D sources
            and dedupes against everything we've covered. Select the ideas you want,
            click <em>Send to draft</em>, then watch the worker panel draft + edit them.
          </p>
          <p className="mt-2 text-xs text-muted-foreground max-w-xl">
            Ideas are pre-articles. Approved ideas become writer tasks (see <Link to="/admin/generate" className="underline">Quick generate</Link>), which become drafts on the <Link to="/admin/articles" search={{ filter: "all" as const, sort: "published_at" as const, dir: "desc" as const, page: 1, pageSize: 25 as const }} className="underline">Articles</Link> tab.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => discover.mutate()}
            disabled={discover.isPending}
            className="border border-border px-4 py-2 text-sm hover:bg-paper-soft disabled:opacity-50"
          >
            {discover.isPending ? "Scanning…" : "Run discovery now"}
          </button>
          <button
            onClick={() => processOne.mutate()}
            disabled={processOne.isPending}
            className="border border-border px-4 py-2 text-sm hover:bg-paper-soft disabled:opacity-50"
          >
            {processOne.isPending ? "Working…" : "Process next task"}
          </button>
        </div>
      </header>

      <WorkerPanel />


      <div className="flex items-center gap-3 border-b border-border">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => {
              setStatus(t.value);
              clearSel();
            }}
            className={
              "px-3 py-2 text-sm border-b-2 -mb-px " +
              (status === t.value
                ? "border-foreground text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-2 bg-paper-soft border border-border p-3 text-sm">
          <span className="font-medium">{selected.size} selected</span>
          <button
            onClick={() => decide.mutate("approved")}
            className="ml-auto px-3 py-1.5 bg-foreground text-background"
          >
            Approve
          </button>
          <button
            onClick={() => send.mutate()}
            disabled={send.isPending}
            className="px-3 py-1.5 border border-border hover:bg-background"
          >
            Send to draft
          </button>
          <button
            onClick={() => decide.mutate("rejected")}
            className="px-3 py-1.5 border border-border hover:bg-background"
          >
            Reject
          </button>
          <button onClick={clearSel} className="px-3 py-1.5 text-muted-foreground">
            Clear
          </button>
        </div>
      )}

      <div className="divide-y divide-border border border-border">
        {(!ideas || ideas.length === 0) && (
          <div className="p-6 text-sm text-muted-foreground">No ideas in this state.</div>
        )}
        {ideas?.map((idea: any) => (
          <label
            key={idea.id}
            className="flex gap-4 p-4 cursor-pointer hover:bg-paper-soft"
          >
            <input
              type="checkbox"
              checked={selected.has(idea.id)}
              onChange={() => toggle(idea.id)}
              className="mt-1.5"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                <span className="uppercase tracking-wider">tier {idea.source_tier ?? "?"}</span>
                <span>·</span>
                <span>{idea.suggested_category_slug}</span>
                <span>·</span>
                <span>novelty {idea.novelty_score}</span>
                {idea.urgency !== "normal" && (
                  <>
                    <span>·</span>
                    <span
                      className={
                        idea.urgency === "breaking" ? "text-red-700 font-medium" : ""
                      }
                    >
                      {idea.urgency}
                    </span>
                  </>
                )}
                <span>·</span>
                <span>{formatDate(idea.discovered_at)}</span>
              </div>
              <h3 className="mt-1 font-medium">{idea.title}</h3>
              {idea.summary && (
                <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                  {idea.summary}
                </p>
              )}
              {idea.angle && (
                <p className="mt-1 text-sm italic">Angle: {idea.angle}</p>
              )}
              <div className="mt-2 flex gap-3 text-xs">
                {idea.source_url && (
                  <a
                    href={idea.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-muted-foreground hover:text-foreground underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Source ↗
                  </a>
                )}
                {idea.source_name && (
                  <span className="text-muted-foreground">{idea.source_name}</span>
                )}
                {idea.focus_keyword_guess && (
                  <span className="text-muted-foreground">
                    KW: {idea.focus_keyword_guess}
                  </span>
                )}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}
